import { useEffect, useState } from 'react';
import { cn } from '@herit-fe/heritage-ui';

/** 슬롯 전환 시간(ms) — 아래 `MOTION` 의 `duration-[450ms]` 와 **같은 값**이어야 되감기 타이밍이 맞는다. */
const TRANSITION_MS = 450;
const DEFAULT_INTERVAL_MS = 7000;
/**
 * 슬라이드·페이드·스케일이 **같은 시간·같은 이징**을 쓰도록 한 곳에 모은 전환 설정. 따로 두면 트랙은
 * 감속하는데 페이드는 등속이라 두 동작이 어긋나 보인다. (Tailwind 스캐너가 읽도록 문자열 리터럴로 유지.)
 */
const MOTION =
  'duration-[550ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none' as const;

export interface RollingTextProps {
  /** 순환 표시할 값들(예: `['100%', '200kW']`). 1개면 정적, 0개면 미렌더. */
  items: string[];
  /** 값 전환 주기(ms). 기본 3초. */
  intervalMs?: number;
  /**
   * 텍스트 스타일 지정 자리 — 폰트 크기·굵기·색은 **호출처가 소유**한다(컴포넌트는 자체 폰트 스타일 없음).
   * 여기 주든 부모에서 상속시키든 sizer 가 같은 폰트로 측정하므로 자리 확보 폭도 함께 따라간다.
   */
  className?: string;
}

/**
 * 텍스트 롤링 — 값 배열을 아래에서 위로 밀어올리며 무한 순환한다. 값만 내려주면 되는 표현 전용 컴포넌트로,
 * 타이머·되감기는 전부 내부 소유(호출처는 배열과 주기만 안다).
 *
 * **움직임 3겹** — 슬라이드(트랙 translate) + 페이드(현재 슬롯만 불투명) + 스케일(들어오며 30%→100%,
 * 나갈 때 그 역순). 셋 다 같은 duration 이라 한 동작으로 읽힌다.
 *
 * **자리 확보** — 보이지 않는 sizer 로 모든 값을 한 셀에 겹쳐 브라우저가 최댓값을 잡게 한다. 가장 긴 값 기준
 * 폭·높이가 처음부터 고정돼, 짧은 값 차례에도 주변 레이아웃이 흔들리지 않는다.
 *
 * **한 슬롯 = 정확히 한 줄** — 트랙 높이를 `inset-0`(=sizer 한 줄)로 고정하고 `grid-auto-rows:100%` 로 각
 * 슬롯을 그 높이에 맞춘다. 그래서 `translateY(-100%)` 한 번이 정확히 한 칸이다(`lh` 단위·픽셀 측정 불필요).
 *
 * **무한 순환** — 트랙 끝에 첫 값을 한 번 더 복제해 두고 마지막→복제본까지 정방향으로 굴린 뒤, 전환이 끝나면
 * 애니메이션을 끄고 0 으로 되감는다. 되감기를 애니메이션하면 전 슬롯을 거꾸로 훑는 게 보인다.
 *
 * 전환은 클래스(`transition-transform`)가 소유 — 인라인 style 로 넣으면 `motion-reduce` 가 못 덮는다.
 */
export function RollingText({
  items,
  intervalMs = DEFAULT_INTERVAL_MS,
  className,
}: RollingTextProps) {
  const count = items.length;
  // 값 동일성 키 — 호출처가 매 렌더 새 배열 리터럴을 넘겨도(대부분 그렇다) 타이머가 재시작되지 않게 한다.
  const itemsKey = items.join(' ');
  const [index, setIndex] = useState(0);
  const [animated, setAnimated] = useState(true);

  // 값 자체가 바뀌면 처음 슬롯부터 다시 — 렌더 중 조정(React 의 "props 변화로 state 리셋" 패턴). effect 로
  // 하면 이전 값 기준으로 한 프레임 그려진 뒤 되돌아간다(항목 수가 줄면 빈 슬롯이 잠깐 보인다).
  const [renderedKey, setRenderedKey] = useState(itemsKey);
  if (renderedKey !== itemsKey) {
    setRenderedKey(itemsKey);
    setIndex(0);
    setAnimated(true);
  }

  useEffect(() => {
    if (count < 2) return;
    const timer = setInterval(() => setIndex((prev) => prev + 1), intervalMs);
    return () => clearInterval(timer);
  }, [itemsKey, count, intervalMs]);

  // 복제 슬롯(index === count)까지 굴렀으면 전환이 끝난 뒤 무애니메이션으로 0 되감기.
  useEffect(() => {
    if (index < count) return;
    const timer = setTimeout(() => {
      setAnimated(false);
      setIndex(0);
    }, TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [index, count]);

  // 되감기가 화면에 반영된 **다음** 프레임에 애니메이션 재개 — 같은 프레임에 켜면 되감기가 그대로 보인다.
  useEffect(() => {
    if (animated) return;
    const frame = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(frame);
  }, [animated]);

  if (count === 0) return null;
  if (count === 1) return <span className={className}>{items[0]}</span>;

  return (
    <span
      // 스크린리더는 굴러가는 값을 쫓을 수 없다 → 트랙·sizer 는 숨기고 전체 값을 라벨로 한 번에 준다.
      aria-label={items.join(', ')}
      className={cn('relative inline-grid overflow-hidden align-middle', className)}
    >
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          aria-hidden
          className="invisible col-start-1 row-start-1 whitespace-nowrap"
        >
          {item}
        </span>
      ))}
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 grid [grid-auto-rows:100%]',
          'transition-transform',
          MOTION,
          !animated && 'transition-none',
        )}
        style={{ transform: `translateY(-${index * 100}%)` }}
      >
        {[...items, items[0]].map((item, i) => (
          // 슬롯(정적) — 트랙 한 칸의 자리만 잡는다. 폭이 컴포넌트 전체라 여기에 scale 을 걸면 **텍스트가
          // 아니라 빈 칸까지 포함한 박스** 기준으로 줄어든다(글자가 옆으로 끌려가 보인다).
          <span key={`${item}-${i}`} className="flex items-center justify-end">
            {/* 실제 움직이는 대상 — shrink-to-fit 이라 기준점(기본 center)이 곧 글자 중심이다.
                밀려 올라오며 동시에 서서히 나타나고(커지며)/사라진다. 되감기 프레임은 트랙 transform 과
                같이 전환을 꺼야 한다(깜빡임 방지).
                ⚠️ 전환 목록에 `scale` 필수 — Tailwind 4 의 `scale-*` 는 `transform` 이 아니라 **`scale`
                프로퍼티**를 쓴다. 빠뜨리면 크기만 전환 없이 튀어 "팍" 사라지는 것처럼 보인다. */}
            <span
              className={cn(
                'whitespace-nowrap transition-[opacity,transform,scale]',
                MOTION, // 트랙 슬라이드와 동일 타이밍 — 세 움직임이 한 동작으로 읽히게.
                !animated && 'transition-none',
                i === index ? 'scale-100 opacity-100' : 'scale-30 opacity-0',
              )}
            >
              {item}
            </span>
          </span>
        ))}
      </span>
    </span>
  );
}
